import { useNavigate } from 'react-router-dom';
import { EditIcon } from '@chakra-ui/icons';
import {
  Button,
  Card,
  CardBody,
  Divider,
  HStack,
  Image,
  Text,
  VStack,
} from '@chakra-ui/react';
// @ts-ignore
import DefaultLogo from '../../assets/icons/DefaultLogo.svg';
import { updateAppDetails } from '../../redux/appBuilder/appDetails';
import { Application } from '../../redux/appBuilder/appDetails/types';
import {
  updateAppActiveStep,
  updateAppEditAction,
  updateAppType,
} from '../../redux/appBuilder/stepperDetails';
import { useAppDispatch } from '../../redux/hooks';

interface ApplicationCardProps {
  appDetail?: Application | any;
  pointerCursor?: boolean;
  onSelect?: (id: string) => void;
  fromAdminDashboard?: boolean;
}
export const ApplicationCard = ({
  appDetail,
  pointerCursor,
  onSelect,
  fromAdminDashboard,
}: ApplicationCardProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleViewCode = () => {
    if (!appDetail.appType) {
      dispatch(updateAppDetails({ ...appDetail, appType: 'WEB' }));
      dispatch(updateAppType('WEB'));
    } else {
      dispatch(updateAppDetails({ ...appDetail }));
      dispatch(updateAppType(appDetail.appType));
    }

    navigate(`/my-applications/code-preview/${appDetail?.appId}`);
  };

  const handleEditClick = () => {
    dispatch(updateAppActiveStep(1));
    dispatch(updateAppEditAction(true));
    // add appType as WEB if not present in appDetail while updateAppDetails
    if (!appDetail.appType) {
      dispatch(updateAppDetails({ ...appDetail, appType: 'WEB' }));
      dispatch(updateAppType('WEB'));
    } else {
      dispatch(updateAppDetails({ ...appDetail }));
      dispatch(updateAppType(appDetail.appType));
    }

    navigate(`/edit-application/${appDetail.appId}`);
  };

  const handleEditClickFromAdminDashboard = () => {
    navigate(`/admin-edit-application/${appDetail.appId}`);
  };

  const handleClick = (e: any) => {
    if (!e.target.closest('[data-clickable="false"]')) {
      // check if onSelect is a function is passed in props
      if (onSelect && typeof onSelect === 'function') {
        onSelect(appDetail.appId);
      }
    }
  };

  return (
    <Card
      py={4}
      px={6}
      variant="filled"
      bgColor="#f7f9fb"
      borderRadius="16px"
      border="1.5px solid #f7f9fb"
      cursor={pointerCursor ? 'pointer' : 'default'}
      _hover={{ borderColor: 'lightgray' }}
    >
      <CardBody overflow="hidden" onClick={handleClick} p={0}>
        <VStack
          align="flex-start"
          w="100%"
          h="100%"
          spacing={1}
          justifyContent="space-between"
        >
          <HStack
            w="100%"
            h="fit-content"
            justifyContent="space-between"
            alignItems="center"
          >
            <VStack w="80%" align="flex-start" justifyContent={'center'} spacing={1.5}>
              <Text
                className="small-font"
                color="black"
                fontFamily="inter"
                maxWidth="85%"
                isTruncated
                title={appDetail?.name}
                style={{ fontWeight: 650 }}
              >
                {appDetail?.name}
              </Text>
              <Text
                className="xx-small-font"
                color="gray"
                fontFamily="inter"
                maxWidth="85%"
                isTruncated
                title={appDetail?.description}
                fontWeight="normal"
              >
                {appDetail?.description}
              </Text>
            </VStack>
            <Image
              src={appDetail?.appImageUrl ? appDetail.appImageUrl : DefaultLogo}
              alt="logo"
              borderRadius="8px"
              w="40px"
              h="40px"
            />
          </HStack>
          <HStack w="100%" my="12px" justifyContent="space-between">
            <VStack alignItems="flex-start" spacing={2}>
              <Text className="xx-small-font" color="black.800" fontFamily="inter">
                Claims
              </Text>
              <Text
                className="small-font"
                color="black"
                fontWeight={'600'}
                fontSize={'20px'}
                fontFamily="inter"
              >
                {appDetail?.totalSessions || 0}
              </Text>
            </VStack>
            <Divider
              orientation="vertical"
              borderColor="black.700" // Custom color
              borderWidth="0.6px" // Custom thickness
            />

            {/* Success rate section - hidden for now
						<VStack
							alignItems='flex-start'
							spacing={2}>
							<Text
								className='xx-small-font'
								color='black.800'
								fontFamily='inter'>
								Success Rate
							</Text>
							<Text
								className='small-font'
								color='black'
								fontWeight={'600'}
								fontSize={'20px'}
								fontFamily='inter'>
								{Math.round(((appDetail?.successSessions / appDetail?.totalSessions) * 100) * 100) / 100 || 0}%
							</Text>
						</VStack>
						*/}
          </HStack>
          <HStack
            w="100%"
            data-clickable="false"
            justifyContent={fromAdminDashboard ? 'center' : 'space-between'}
          >
            {!fromAdminDashboard && (
              <Button
                justifyContent="space-between"
                bgColor="transparent"
                p={0}
                borderRadius="8px"
                _hover={{ bgColor: 'transparent' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewCode();
                }}
                size="sm"
              >
                <Text
                  className="xx-small-font"
                  color="gray"
                  textDecoration="underline"
                  _hover={{ color: '#242323' }}
                  fontFamily="sans-serif"
                >
                  View Code
                </Text>
              </Button>
            )}
            <Button
              justifyContent="space-between"
              bgColor="transparent"
              p={0}
              borderRadius="8px"
              onClick={(e) => {
                e.stopPropagation();
                if (fromAdminDashboard) {
                  handleEditClickFromAdminDashboard();
                } else {
                  handleEditClick();
                }
              }}
              _hover={{ bgColor: 'transparent' }}
              size="sm"
            >
              {/* Add left icon to the button */}
              <EditIcon boxSize={3} _hover={{ color: '#242323' }} color="grey" mr="4px" />
              <Text
                className="xx-small-font"
                color="grey"
                // add underline in chakra
                textDecoration="underline"
                _hover={{ color: '#242323' }}
                fontFamily="sans-serif"
              >
                Edit
              </Text>
            </Button>
          </HStack>
        </VStack>
      </CardBody>
    </Card>
  );
};
